import React from "react";
import { GripVertical } from "lucide-react";
import s from "@/pages/ProductionSuite.module.css";

/**
 * PSTrackHeaders — the left column of the DAW showing every track's
 * label, mute / solo controls, right-click Insert/Delete menu, and the
 * drag-to-reorder grip. Extracted from ProductionSuite.jsx to keep the
 * page-level file focused on transport / timeline / audio wiring.
 *
 * All state (tracks, selection, mute/solo maps) is owned by the parent
 * because the timeline column shares the same tracks array for cross-
 * track clip drag hit-testing.
 */
export default function PSTrackHeaders({
  tracks,
  selectedTrackId,
  onSelectTrack,
  mute,
  solo,
  onToggleMute,
  onToggleSolo,
  onAddTrack,
  onInsertTrackAt,
  onRemoveTrack,
  // Update #155 — drag-to-reorder wiring
  reorderingId,
  onGripPointerDown,
  dropLineRef,
  trackLabelsRef,
  // Right-click menu state — kept lifted so the backdrop can overlay
  // the entire page.
  trackMenu,
  onOpenTrackMenu,
  onCloseTrackMenu,
}) {
  return (
    <>
      {/* Left column: tracks list */}
      <div className={s.trackLabels} data-testid="ps-tracks" ref={trackLabelsRef}>
        <div className={s.labelHeader}>
          TRACKS
          <button
            type="button"
            onClick={onAddTrack}
            className={s.addTrackBtn}
            data-testid="ps-add-track"
            title="Add track"
          >
            +
          </button>
        </div>
        {/* Drop indicator line for track reorder drag */}
        <div
          ref={dropLineRef}
          className={s.trackDropLine}
          data-testid="ps-track-drop-line"
          aria-hidden
        />
        {tracks.map((t) => {
          const selected = selectedTrackId === t.id;
          const reordering = reorderingId === t.id;
          return (
            <div
              key={t.id}
              data-ps-track-row
              className={`${s.trackRow} ${selected ? s.trackRowSelected : ""} ${reordering ? s.trackRowReordering : ""}`}
              onClick={() => onSelectTrack(t.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                onSelectTrack(t.id);
                onOpenTrackMenu({ trackId: t.id, x: e.clientX, y: e.clientY });
              }}
              data-testid={`ps-track-label-${t.id}`}
            >
              <div
                className={s.trackReorderGrip}
                onPointerDown={(e) => onGripPointerDown(e, t.id)}
                onClick={(e) => e.stopPropagation()}
                title="Drag to reorder track"
                data-testid={`ps-track-grip-${t.id}`}
              >
                <div className={s.trackBar} style={{ background: t.color }} />
                <GripVertical size={11} className={s.trackReorderGripIcon} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={s.trackName}>{t.name}</div>
                <div className={s.trackType}>{t.type}</div>
              </div>
              <div className={s.trackControls}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleMute(t.id); }}
                  className={`${s.msBtn} ${mute[t.id] ? s.msBtnMuteActive : s.msBtnMute}`}
                  title="Mute"
                  data-testid={`ps-mute-${t.id}`}
                >
                  M
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleSolo(t.id); }}
                  className={`${s.msBtn} ${solo[t.id] ? s.msBtnSoloActive : s.msBtnSolo}`}
                  title="Solo"
                  data-testid={`ps-solo-${t.id}`}
                >
                  S
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Right-click context menu — rendered as a page-level overlay
          so it isn't clipped by the tracks column's overflow:auto. */}
      {trackMenu && (
        <>
          <div
            className={s.trackMenuBackdrop}
            onClick={onCloseTrackMenu}
            onContextMenu={(e) => { e.preventDefault(); onCloseTrackMenu(); }}
            data-testid="ps-track-menu-backdrop"
          />
          <div
            className={s.trackMenu}
            style={{ left: trackMenu.x, top: trackMenu.y }}
            data-testid="ps-track-menu"
            role="menu"
          >
            <button
              type="button"
              className={s.trackMenuItem}
              onClick={() => onInsertTrackAt(trackMenu.trackId, "above")}
              data-testid="ps-track-menu-insert-above"
            >
              Insert track above
            </button>
            <button
              type="button"
              className={s.trackMenuItem}
              onClick={() => onInsertTrackAt(trackMenu.trackId, "below")}
              data-testid="ps-track-menu-insert-below"
            >
              Insert track below
            </button>
            <div className={s.trackMenuDivider} />
            <button
              type="button"
              className={`${s.trackMenuItem} ${s.trackMenuItemDanger}`}
              onClick={() => onRemoveTrack(trackMenu.trackId)}
              disabled={tracks.length <= 1}
              data-testid="ps-track-menu-delete"
            >
              Delete track
            </button>
          </div>
        </>
      )}
    </>
  );
}
